/* Virtual try-on trigger + modal on the PDP: customer uploads/captures a
 * photo, it's composited onto the garment via raftor_webhq's vendor-agnostic
 * try-on pipeline (catalog/tryon.py + tryon/adapters/*).
 *
 * Gated on window.rfhqTryOnEnabled (Raftor Storefront Feature Settings) and
 * per-item-group eligibility (raftor_webhq.catalog.tryon.get_tryon_eligibility).
 * raftor_webhq owns the job/vendor logic; this file only renders the upload
 * UI and polls for a result.
 */

import { isPdp, getPdpItemCode, getPdpMainBlock } from './pdp_context.js';

var POLL_INTERVAL_MS = 2000;
var POLL_TIMEOUT_MS = 60000;

// Camera glyph, not the Trial Room hanger (product_card/card/layout/root.js)
// - a different icon for a different feature, so the two "try this on"-
// sounding actions don't read as the same button. Same stroke-icon
// convention every other RFHQ icon button already uses (currentColor,
// stroke-width 2, round caps).
var TRYON_ICON =
	'<path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z"/>' +
	'<circle cx="12" cy="13" r="3"/>';

frappe.ready(function () {
	if (!window.rfhqTryOnEnabled) return;
	if (!isPdp()) return;

	var itemCode = getPdpItemCode();
	if (!itemCode) return;

	frappe.call({
		method: 'raftor_webhq.catalog.tryon.get_tryon_eligibility',
		args: { item_code: itemCode },
		callback: function (r) {
			if (!r || !r.message || !r.message.eligible) return;
			mountTrigger(itemCode);
		},
	});
});

function mountTrigger(itemCode) {
	var anchor = getPdpMainBlock();
	if (!anchor) return;

	// Icon button, not a labelled full-width button - matches the product
	// card's own icon-badge convention (.rfhq-pc-trialroom-toggle/.rfhq-pc-wish:
	// a small circular button, translucent --rfhq-card-surface fill, a single
	// stroke-icon glyph, aria-label carrying the text instead of visible
	// label text) rather than reading as a second full-width CTA next to
	// Add to Cart.
	var trigger = document.createElement('button');
	trigger.type = 'button';
	trigger.className = 'rfhq-pdp-tryon-trigger';
	trigger.setAttribute('aria-label', __('Try It On'));
	trigger.title = __('Try It On');
	trigger.innerHTML = '<svg class="icon sm" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' + TRYON_ICON + '</svg>';
	trigger.addEventListener('click', function () {
		openModal(itemCode);
	});

	anchor.appendChild(trigger);
}

function openModal(itemCode) {
	var backdrop = document.createElement('div');
	backdrop.className = 'rfhq-pdp-tryon-modal-backdrop';

	var modal = document.createElement('div');
	modal.className = 'rfhq-pdp-tryon-modal';
	modal.innerHTML =
		'<button type="button" class="rfhq-pdp-tryon-modal-close" aria-label="' + __('Close') + '">&times;</button>' +
		'<h4>' + __('Try It On') + '</h4>' +
		'<p>' + __('Upload a photo to see how this looks on you.') + '</p>' +
		'<input type="file" accept="image/*" capture="user" class="rfhq-pdp-tryon-file-input">' +
		'<div class="rfhq-pdp-tryon-preview"></div>' +
		'<div class="rfhq-pdp-tryon-status"></div>' +
		'<div class="rfhq-pdp-tryon-result"></div>';

	backdrop.appendChild(modal);
	document.body.appendChild(backdrop);

	function close() {
		backdrop.remove();
	}
	backdrop.addEventListener('click', function (e) {
		if (e.target === backdrop) close();
	});
	modal.querySelector('.rfhq-pdp-tryon-modal-close').addEventListener('click', close);

	var fileInput = modal.querySelector('.rfhq-pdp-tryon-file-input');
	var previewEl = modal.querySelector('.rfhq-pdp-tryon-preview');
	var statusEl = modal.querySelector('.rfhq-pdp-tryon-status');
	var resultEl = modal.querySelector('.rfhq-pdp-tryon-result');

	fileInput.addEventListener('change', function () {
		var file = fileInput.files && fileInput.files[0];
		if (!file) return;

		previewEl.innerHTML = '';
		var img = document.createElement('img');
		img.src = URL.createObjectURL(file);
		previewEl.appendChild(img);

		uploadAndStart(file, itemCode, statusEl, resultEl);
	});
}

function uploadAndStart(file, itemCode, statusEl, resultEl) {
	statusEl.textContent = __('Uploading photo...');

	var formData = new FormData();
	formData.append('file', file);
	formData.append('is_private', 1);

	fetch('/api/method/upload_file', {
		method: 'POST',
		headers: { 'X-Frappe-CSRF-Token': frappe.csrf_token },
		body: formData,
	})
		.then(function (res) { return res.json(); })
		.then(function (data) {
			var fileUrl = data && data.message && data.message.file_url;
			if (!fileUrl) throw new Error('upload failed');

			statusEl.textContent = __('Processing...');
			return frappe.call({
				method: 'raftor_webhq.catalog.tryon.start_tryon',
				args: { item_code: itemCode, source_image: fileUrl },
			});
		})
		.then(function (r) {
			var message = r && r.message;
			if (!message) throw new Error('no response');
			if (message.status === 'Completed' || message.status === 'Failed') {
				renderStatus(message, statusEl, resultEl);
			} else {
				pollStatus(message.job_id, statusEl, resultEl, Date.now());
			}
		})
		.catch(function () {
			statusEl.textContent = __('Something went wrong. Please try again.');
		});
}

function pollStatus(jobId, statusEl, resultEl, startedAt) {
	if (Date.now() - startedAt > POLL_TIMEOUT_MS) {
		statusEl.textContent = __('This is taking longer than expected. Please check back shortly.');
		return;
	}

	frappe.call({
		method: 'raftor_webhq.catalog.tryon.get_tryon_status',
		args: { job_id: jobId },
		callback: function (r) {
			var message = r && r.message;
			if (!message) return;
			if (message.status === 'Completed' || message.status === 'Failed') {
				renderStatus(message, statusEl, resultEl);
			} else {
				statusEl.textContent = __('Processing...');
				setTimeout(function () {
					pollStatus(jobId, statusEl, resultEl, startedAt);
				}, POLL_INTERVAL_MS);
			}
		},
	});
}

function renderStatus(message, statusEl, resultEl) {
	if (message.status === 'Completed' && message.result_image) {
		statusEl.textContent = '';
		var img = document.createElement('img');
		img.src = message.result_image;
		resultEl.innerHTML = '';
		resultEl.appendChild(img);
	} else {
		statusEl.textContent = message.error_message || __('Try-on could not be completed.');
	}
}
